import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';

import { useFocusEffect, useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';

import {
  isEndedDisplayStatus,
  myStudiesTab,
  StudyParticipantWithDisplayStatus,
  toStudyDisplayStatus
} from '../../common/studyParticipantDisplayStatus';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import AlertDialog from '../../components/AlertDialog';
import Button from '../../components/Button';
import HeaderTabView from '../../components/HeaderTabView';
import Spacer from '../../components/Spacer';
import { B, P } from '../../components/Typography';
import { useUserContext } from '../../context/UserContext';
import { StudyParticipant } from '../../models/StudyParticipant';
import StudyParticipantCard from '../study/cards/StudyParticipantCard';
import useGetStudyFavorites from '../../hooks/user-favorite/useGetStudyFavorites';
import StudyCard from '../study/cards/StudyCard';
import StudyIntroModal from '../modals/StudyIntroModal';
import { UserStudyFavorite } from '../../models/UserStudyFavorite';
import elementIDs from '../../../elementIDs';

const StyledScrollView = styled.ScrollView`
  background: #fdfbf7;
`;

const StyledView = styled.View`
  flex: 1;
  padding: 24px 16px 24px 16px;
  max-width: 1080px;
  width: 100%;
  align-self: center;
`;

const StyledCardContainerView = styled.View`
  padding: 10px 20px 0 20px;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  align-self: center;
  align-items: center;
  justify-content: space-evenly;
`;

const StyledEmptyStateView = styled.View`
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-bottom: 32px;
`;

const StyledEmptyStateContentView = styled.View`
  max-width: 420px;
  text-align: center;
`;

const StyledStartStudyButton = styled(Button)`
  min-width: 180px;
  margin: 0 auto;
  margin-bottom: 32px;
`;

const StyledP = styled(P)`
  font-size: 14px;
  padding: 4px;
  margin-bottom: 10px;
`;

const StyledStudyView = styled.View<{ singleColumn: boolean }>`
  width: ${p => (p.singleColumn ? '100%' : 'auto')};
`;

enum FilterParams {
  Active = 'Active',
  Past = 'Past',
  Favorites = 'Favorites'
}

const MyStudiesScreen: React.FC = () => {
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const navigation = useNavigation<StackNavigationProp<any>>();
  const [openAlert, setOpenAlert] = useState(false);
  const {
    studyParticipants,
    loadingStudyParticipants,
    updateUserSession,
    accessToken,
    user
  } = useUserContext();
  const [studyParticipantListWithStatus, setStudyParticipantListWithStatus] =
    useState([] as StudyParticipantWithDisplayStatus[]);
  const [filteredStudyParticipantList, setFilteredStudyParticipantList] =
    useState([] as StudyParticipant[]);
  const [favoriteStudies, setFavoriteStudies] = useState<UserStudyFavorite[]>(
    []
  );
  const [filterValue, setFilterValue] = useState<FilterParams>(
    FilterParams.Active
  );
  const { studyFavorites, getStudyFavorites } = useGetStudyFavorites(
    user?.id || -1
  );

  const [selectedStudyId, setSelectedStudyId] = useState<number>();

  useFocusEffect(
    useCallback(() => {
      if (user?.id) {
        updateUserSession();
        getStudyFavorites();
      }
    }, [user?.id, updateUserSession, getStudyFavorites])
  );

  useEffect(() => {
    let studies = [];
    switch (filterValue) {
      case FilterParams.Past:
        studies = studyParticipantListWithStatus.filter(
          study => myStudiesTab(study) === 'past'
        );
        break;
      case FilterParams.Favorites:
        {
          studies = [...studyParticipantListWithStatus].filter(
            studyParticipant =>
              studyFavorites?.some(
                studyFavorite =>
                  studyFavorite.studyParticipantId === studyParticipant.id
              )
          );
          setFavoriteStudies(
            studyFavorites?.filter(studyFavorite => !!studyFavorite.studyId) ??
              []
          );
        }
        break;
      default:
        studies = [...studyParticipantListWithStatus]
          .filter(study => myStudiesTab(study) === 'active')
          .sort(
            (a: any, b: any) =>
              new Date(b.created).getTime() - new Date(a.created).getTime()
          );
        break;
    }
    setFilteredStudyParticipantList([...studies]);
    return () => {
      studies = [];
    };
  }, [filterValue, studyFavorites, studyParticipantListWithStatus]);

  // TODO: findStudies returns a paginated list; will probably need an endpoint
  // to return these stats below.
  // console.log('studyList', studyList);
  const { active } = useMemo(() => {
    const studyParticipantsWithStatus: StudyParticipantWithDisplayStatus[] = [];
    // const sponsoredStudies: Study[] = [];
    // let activeSponsoredStudy = false;
    const total = studyParticipants?.length || 0;
    let planning = 0;
    let active = 0;
    let completed = 0;
    // let hasActiveConsumerStudy = false;
    for (const studyParticipant of studyParticipants || []) {
      const displayStatus = toStudyDisplayStatus(studyParticipant);
      studyParticipantsWithStatus.push({ ...studyParticipant, displayStatus });
      if (isEndedDisplayStatus(displayStatus)) completed++;
      else if (displayStatus === 'progress') active++;
      else planning++;
    }
    // if (hasActiveConsumerStudy) {
    //   // filter out all sponsored studies if an active/planning consumer study
    //   // and instead show alert that user qualifies for a sponsored study
    //   studyParticipantsWithStatus = studyParticipantsWithStatus.filter(sp => {
    //     if (sp.study?.type === StudyType.Sponsor && sp.status !== 'completed') {
    //       switch (sp.status) {
    //         case 'active':
    //           active--;
    //           break;
    //         case 'planning':
    //           planning--;
    //           break;
    //       }
    //     }
    //     // continue to show all completed studies
    //     return (
    //       sp.study?.type !== StudyType.Sponsor || sp.status === 'completed'
    //     );
    //   });
    // }
    setStudyParticipantListWithStatus(studyParticipantsWithStatus);
    setFilteredStudyParticipantList(studyParticipantsWithStatus);
    return {
      active,
      planning,
      completed,
      total
    };
  }, [studyParticipants]);
  return (
    <>
      <StyledScrollView
        scrollIndicatorInsets={{ right: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        <StyledView>
          {/* {!activeSponsoredStudy &&
            sponsoredStudies.length > 0 &&
            active > 0 && (
              <>
                <QualifySponsorStudyCard
                  studyParticipant={sponsoredStudies[0].studyParticipants[0]}
                  onClick={studyIntake => {
                    navigation.navigate('ModalNavigator', {
                      screen: 'FormTemplate',
                      params: {
                        entityDataId: studyIntake.id,
                        studyParticipantId:
                          sponsoredStudies[0].studyParticipants[0].id,
                        formTemplateId:
                          sponsoredStudies[0]
                            ?.ongoingStudyNotificationFormTemplateId
                      }
                    });
                  }}
                />
                <Spacer size={4} />
              </>
            )} */}

          {Boolean(studyParticipants?.length) && (
            <>
              <HeaderTabView
                tabs={['Active', 'Past', 'Favorites']}
                onTabChange={(_, tab) => {
                  setFilterValue(
                    FilterParams[tab as keyof typeof FilterParams]
                  );
                }}
              />
              <Spacer size={3} />
            </>
          )}
          <StyledCardContainerView>
            {filterValue === FilterParams.Favorites &&
              favoriteStudies &&
              favoriteStudies?.length > 0 &&
              favoriteStudies?.map(favoriteStudy => (
                <StyledStudyView
                  singleColumn={!isDesktop}
                  key={favoriteStudy.id}
                >
                  <StudyCard
                    studyId={favoriteStudy.id}
                    favorite={true}
                    onFavorite={() => getStudyFavorites()}
                    onSelect={() => setSelectedStudyId(favoriteStudy.id)}
                  />
                  <Spacer />
                </StyledStudyView>
              ))}
            {filteredStudyParticipantList?.length
              ? filteredStudyParticipantList.map(studyParticipant => (
                  <StyledStudyView
                    singleColumn={!isDesktop}
                    key={studyParticipant.id}
                  >
                    <StudyParticipantCard
                      studyParticipant={studyParticipant}
                      accessory={undefined}
                      imageUrl={
                        studyParticipant.study?.backgroundImageUrl
                          ? studyParticipant.study.backgroundImageUrl +
                            '&' +
                            `token=${accessToken}`
                          : undefined
                      }
                      favorite={studyFavorites?.some(
                        favorite =>
                          favorite.studyParticipantId === studyParticipant.id
                      )}
                      onFavorite={() => getStudyFavorites()}
                      textColor={studyParticipant.study?.foregroundColor}
                      backgroundColor={studyParticipant.study?.backgroundColor}
                    />
                    <Spacer />
                  </StyledStudyView>
                ))
              : filteredStudyParticipantList?.length === 0 &&
                !(
                  favoriteStudies?.length > 0 &&
                  filterValue === FilterParams.Favorites
                ) && (
                  <StyledEmptyStateView>
                    <StyledEmptyStateContentView>
                      <StyledP testID={elementIDs.noStudiesID} color='medium'>
                        {(() => {
                          switch (filterValue) {
                            case FilterParams.Active:
                              return "Looks like you haven't started a study yet.";
                            case FilterParams.Favorites:
                              return "You haven't saved any studies.";
                            case FilterParams.Past:
                              return "You haven't completed any studies yet.";
                          }
                        })()}
                      </StyledP>
                      {filterValue === FilterParams.Active && (
                        <StyledStartStudyButton
                          outlined
                          size='medium'
                          testID={elementIDs.startStudyID}
                          onPress={() => navigation.navigate('DiscoverNav')}
                        >
                          <B>Start a study now</B>
                        </StyledStartStudyButton>
                      )}
                    </StyledEmptyStateContentView>
                  </StyledEmptyStateView>
                )}
          </StyledCardContainerView>
          {/* {(studyParticipants?.length || 0) > 0 && (
            <StyledNewStudyView
              activeOpacity={0.7}
              onPress={() => {
                if (planning > 0 || active > 0) {
                  setOpenAlert(true);
                } else {
                  navigation.navigate('StudiesNav', {
                    screen: 'FormTemplate',
                        params: {
                          formTemplateName: 'Start Study'
                        }
                  });
                }
              }}
            >
              <StyledNewStudyText color='medium'>New Study</StyledNewStudyText>
            </StyledNewStudyView>
          )} */}
        </StyledView>
      </StyledScrollView>
      {selectedStudyId && (
        <StudyIntroModal
          studyId={selectedStudyId}
          visible
          onFavorite={() => getStudyFavorites()}
          onClose={() => setSelectedStudyId(undefined)}
          onRequestClose={() => setSelectedStudyId(undefined)}
        />
      )}
      {openAlert && (
        <AlertDialog
          title='Already in a Study'
          message='Oops! Looks like you already have a study in progress. To support better science, you are limited to one study at a time. The more things you study together, the harder it is to determine what may be leading to changes.'
          onCloseClicked={() => setOpenAlert(false)}
        />
      )}
      {loadingStudyParticipants && <ActivityIndicatorScreen modal />}
    </>
  );
};

export default MyStudiesScreen;
